'use strict';

/**
 * Basic layout view for the import dialog
 *
 * @category   View
 * @module     import
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var ColidoLayoutView = require('base/Colido.LayoutView');
var ReviewObjectsCollection = require('../../collections/review-objects');
var UploadComponent = require('../components/upload');
var MatchComponent = require('../components/match');
var ReviewComponent = require('../components/review');
var ImportModel = require('../../models/import');

var template = require('../../templates/layout/import.hbs');

var ImportLayout = ColidoLayoutView.extend({
    template: template,

    ui: {
        container: '.import-module',
        subline: '.subline',
        containerMatch: '.import-container-match',
        actionNext: '[data-action="next"]',
        actionCancel: '[data-action="cancel"]',
    },

    events: {
        'click @ui.actionNext': 'onActionNext',
        'click @ui.actionCancel': 'onActionCancel',
    },

    regions: {
        uploadRegion: '#region-upload',
        matchRegion: '#region-match',
        reviewRegion: '#region-review',
    },

    childEvents: {
        'component:upload:file:selected': 'onComponentUploadFileSelected',
        'component:upload:state:finished': 'onComponentUploadStateFinished',
        'component:upload:state:error': 'onComponentUploadStateError',
        'component:match:import:start': 'onComponentMatchImportStart',
        'component:review:import:start': 'onComponentReviewImportStart',
        'component:match:field:changed': 'onFieldChanged',
    },

    modelEvents: {
        'change:state': 'stateChanged',
    },

    /**
     * Current step in import process
     * @type {number}
     */
    currentStep: 1,

    /**
     * Max steps
     * @type {number}
     */
    maxStep: 3,

    /**
     * Model for the import
     * @type {ImportModule.ImportModel}
     */
    model: null,

    /**
     * Collection for the review
     * @type {ImportModule.ReviewObjectsCollection}
     */
    reviewCollection: null,

    /**
     * Collection for the fields
     * @type {Colido.Collection}
     */
    fieldsCollection: null,

    /**
     * Components to use in this form
     * @type {Array}
     */
    components: [
        {
            id: 'Upload',
            cls: UploadComponent,
            instance: null,
            region: 'uploadRegion',
            params: {},
        }, {
            id: 'Match',
            cls: MatchComponent,
            instance: null,
            region: 'matchRegion',
            params: {},
        }, {
            id: 'Review',
            cls: ReviewComponent,
            instance: null,
            region: 'reviewRegion',
            params: {},
        },
    ],

    initialize: function(options) {
        ColidoLayoutView.prototype.initialize.apply(this, arguments);

        this.fieldsCollection = this.collection.catalog.getImportFields();

        this.model = new this.collection.model({}, {
            collection: this.collection,
            fieldsCollection: this.fieldsCollection,
        });

        this.listenTo(this.model.matches, 'change', this.onColumnMatchChanged);

        this.reviewCollection = new ReviewObjectsCollection();

        this.initializeComponents();
    },

    onRender: function() {
        this.renderComponents();
    },

    /**
     * Initializes all available components
     */
    initializeComponents: function() {
        _.each(this.components, function(item, idx) {
            var params = {};
            _.extend(params, item.params);

            if (!params.parent) {
                params.parent = this;
            }

            if (!params.step) {
                params.step = idx + 1;
            }

            params.collection = this.model.matches;
            params.model = this.model;
            params.fieldsCollection = this.fieldsCollection;

            if (_.isFunction(item.cls)) {
                this.components[idx].instance = new item.cls(params);
            } else {
                console.error('No function found for: ' + item.id);
                console.info(item);
            }
        }.bind(this));
    },

    /**
     * Renders all available components
     */
    renderComponents: function() {
        _.each(this.components, function(item) {
            this[item.region].show(item.instance);
        }.bind(this));
    },

    /**
     * Returns the component instance for the given ID
     * @param {string} id ID of the component
     * @returns {null}|{object}
     */
    getComponentInstance: function(id) {
        var component = null;
        _.each(this.components, function(item) {
            if (item.id == id) {
                component = item.instance;
            }
        }.bind(this));

        return component;
    },

    /**
     * Action for next step
     * @param event
     */
    onActionNext: function(event) {
        var component = this._getCurrentComponent();
        if (component) {
            component.triggerMethod('parent:proceed');
            this.ui.actionNext.prop('disabled', true);
        }
    },

    /**
     * Action, when import process is canceled
     * @param event
     */
    onActionCancel: function(event) {
        var catalog = this.collection.catalog;
        App.router.navigate('/catalogs/' + catalog.id + '/imports', {trigger: true});
    },

    /**
     * Is triggered by Upload ChildView, when a file has been selected
     */
    onComponentUploadFileSelected: function() {
        this.ui.actionNext.prop('disabled', false);
    },

    /**
     * Is triggered by Upload ChildView, when the upload was successful
     * @param event
     */
    onComponentUploadStateFinished: function(view, data) {
        this.model.set(this.model.parse(data));
        this.collection.add(this.model);

        // Set default attributes
        this.model.set({
            defaultCurrency: App.user.getCurrency() || 'eur',
            defaultUnitSize: App.user.get('defaultSizeUnit') || 'mm',
            defaultUnitWeight: App.user.get('defaultWeightUnit') || 'g',
        })
    },

    /**
     * Is triggered by Upload ChildView when there was an upload error
     */
    onComponentUploadStateError: function() {
        this.ui.actionNext.prop('disabled', true);
    },

    /**
     * Is triggered, when import is started after match. We will disable the next button
     * @param event
     */
    onComponentMatchImportStart: function(event) {
        this.ui.actionNext.prop('disabled', true);
        this.model.set('state', 'mapping');
        this.model.enableValidation();

        this.model.save();
    },

    /**
     * Is triggered, when import is started after review. We will disable the next button
     * @param event
     */
    onComponentReviewImportStart: function(event) {
        this.ui.actionNext.prop('disabled', true);
        this.model.save();
    },

    /**
     * Is triggered, when a column was matched
     * @param model The model that caused the match
     */
    onColumnMatchChanged: function(model) {
        if (this.currentStep == 2) {
            this.ui.actionNext.prop('disabled', !model.collection.selectedColumns());
        }
    },

    /**
     * Is triggered, when a field (column mapping) was changed by the user. We will remap the columns
     * @param {Colido.LayoutView} childView The child view
     * @param {string}            field     The field that was changed
     */
    onFieldChanged: function(childView, field) {
        this.model.set('state', 'remap');
        this.model.disableValidation();
        this.model.save();
    },

    /**
     * Is triggered when a state has changed
     * @param {ImportModel} model The import model
     * @param {string}      state The new state
     */
    stateChanged: function (model, state) {
        switch (state) {
            case 'upload':
                this.setStep(1);
                // App.radio('base').trigger('collection-fields:reset-selection'); // Reset selected fields
                break;
            case 'mapping':
                this.setStep(2);
                break;
            case 'reviewing':
                this.setStep(3);
                break;
            case 'waiting':
            case 'importing':
                App.router.navigate('/catalogs/' + this.collection.catalog.id + '/imports', {trigger: true});

                Messenger().info({
                    message: App.t('collection::import:response:import-started'),
                });
                break;
        }
    },

    /**
     * Sets a new step for the component
     * @param {number} step The new step
     */
    setStep: function(step) {
        // Set class to match container
        this.ui.containerMatch.toggleClass('container-dark', step > 2);

        var stepBefore = this.currentStep;

        if (step <= this.maxStep) {
            // General stuff
            this.currentStep = step;
            this.ui.container.attr('data-step', step);

            // Update step count
            this.ui.subline.html(App.t('collection::import:subline-step', {step: step, stepTotal: this.maxStep}));

            // Region specific stuff
            switch (step) {
                case 1: // Upload
                    this.ui.actionNext.html(App.t('collection::import:button-next')).prop('disabled', true);
                    break;

                case 2: // Match
                    this.ui.actionNext.html(App.t('collection::import:button-next')).prop('disabled', !this.model.matches.selectedColumns());
                    break;

                case 3: // Review
                    this.ui.actionNext.html(App.t('collection::import:button-done')).prop('disabled', false);
                    break;
            }
        }
    },

    /**
     * Returns the instance of the current component
     * @returns {*}
     * @private
     */
    _getCurrentComponent: function() {
        return this.components[this.currentStep - 1].instance;
    },
});

module.exports = ImportLayout;